// Map of TName (integer) -> { hintText, manualSection }
//
// hintText:      short manual-grounded body shown in the Insights
//                Window's hover slot. Michael owns the copy — these
//                entries are transcribed verbatim from
//                hsh-documents/TNAME_HINTS.md (the "TName N — …" prose,
//                minus the leading marker and the trailing "(Manual §…)"
//                citation, which becomes manualSection). Internal-NAMES
//                authoring asides in the source (e.g. "Internal NAMES
//                says Robot") are not player copy and are omitted.
// manualSection: section reference like '§7.2.4' that the manual drawer
//                opens to. ManualLauncher resolves it to the matching
//                heading slug at click time; sections without their own
//                heading fall back to opening the manual at the top.
//
// Coverage: rooms (1–58), action-hex slices (71–76), faction icons
// (77–82), and the full guest deck + ghost cards (84–121) — i.e. every
// TName the data-tname attributes wired in this pass can surface
// (hotel rooms, docked cards, river cards, faction badge, action hex).
// Faction-action tokens (59–70), Snohe denominations, and system/UI
// icons are not yet hovered anywhere, so they're left for a later pass.

export const TEXTURE_HINTS = {
    // ===================== Action Rooms (Manual §6.4) =================
    1: {
        hintText: 'Upgraded Research Action Room. Costs 1 Staff. Staffed '
            + 'grants +2 bonus Research actions per Research Set. Research '
            + 'lets you Acquire a Room from the Game Pool, Upgrade an '
            + 'uninstalled Room, Sell an upgraded uninstalled Room for 1 '
            + 'Snohe, or Process a Snohe Crash for 3.',
        manualSection: '§7.2.1',
    },
    2: {
        hintText: 'Research Action Room. Costs 2 Staff. Staffed grants +1 '
            + 'bonus Research action per Research Set. Upgrade via a '
            + 'Research action to drop the staffing cost.',
        manualSection: '§7.2.1',
    },
    3: {
        hintText: 'Upgraded Lodging Action Room. Costs 1 Staff. Staffed '
            + 'grants +2 bonus Lodging actions per Lodging Set. Each Lodging '
            + 'action Supplies your Hotel (Fate roll for permit '
            + 'gains/upgrades) or Attacks an opponent (Fate roll for permit '
            + 'losses, right-to-left).',
        manualSection: '§7.2.2',
    },
    4: {
        hintText: 'Lodging Action Room. Costs 2 Staff. Staffed grants +1 '
            + 'bonus Lodging action per Lodging Set.',
        manualSection: '§7.2.2',
    },
    5: {
        hintText: 'Upgraded Staff Action Room. Costs 1 Staff. Staffed grants '
            + '+2 bonus Staff actions per Staff Set. Required to Clock In '
            + 'your 5th and 6th Chargeable Staff.',
        manualSection: '§7.2.3',
    },
    6: {
        hintText: 'Staff Action Room. Costs 2 Staff. Staffed grants +1 bonus '
            + 'Staff action per Staff Set. Each Staff action Clocks In one '
            + 'Chargeable Staff from your supply to an available Room slot.',
        manualSection: '§7.2.3',
    },
    7: {
        hintText: 'Upgraded Renovation Action Room. Costs 1 Staff. Staffed '
            + 'grants +2 bonus Renovation actions per Renovation Set.',
        manualSection: '§7.2.4',
    },
    8: {
        hintText: 'Renovation Action Room. Costs 2 Staff. Staffed grants +1 '
            + 'bonus Renovation action per Renovation Set. Each Renovation '
            + 'Installs, Uninstalls, Relocates, or Sells an installed Room '
            + '(Sell yields 3 Snohe).',
        manualSection: '§7.2.4',
    },
    9: {
        hintText: 'Upgraded Advertisement Action Room. Costs 1 Staff. '
            + 'Staffed grants +2 bonus Advertisement actions per '
            + 'Advertisement Set.',
        manualSection: '§7.2.5',
    },
    10: {
        hintText: 'Advertisement Action Room. Costs 2 Staff. Staffed grants '
            + '+1 bonus Advertisement action per Advertisement Set. Each '
            + 'Advertisement targets the outermost Guest in one Orbit Column '
            + 'to Dock, Refer to an opponent, Discard, or fill an empty '
            + 'Orbit.',
        manualSection: '§7.2.5',
    },
    11: {
        hintText: 'Upgraded Ghost Action Room. Costs 1 Staff. Staffed grants '
            + '+2 bonus Ghost actions per Ghost Set.',
        manualSection: '§7.2.6',
    },
    12: {
        hintText: 'Ghost Action Room. Costs 2 Staff. Staffed grants +1 bonus '
            + 'Ghost action per Ghost Set. Each Ghost action Cleanses your '
            + 'own Docks (Fate roll) or Haunts an opponent (Fate roll, adds '
            + 'Ghosts from their supply).',
        manualSection: '§7.2.6',
    },

    // ===================== Amenity Rooms (Manual §6.5) ================
    13: {
        hintText: 'Upgraded Observatory. Costs 1 Staff. Bonus +1 Snohe on '
            + 'top of the base Amenity bonus when scoring a Scientist '
            + '(Science Academy Guild).',
        manualSection: '§6.5',
    },
    14: {
        hintText: 'Observatory. Costs 2 Staff. Base Amenity bonus +1 Snohe '
            + 'per scored Guest; +1 more for Scientists.',
        manualSection: '§6.5',
    },
    15: {
        hintText: 'Upgraded Casino. Costs 1 Staff. Matches the Gambling '
            + 'Commission Guild — extra +1 Snohe on scored Vanguards '
            + '(Stem-themed guests overlap their Guild here).',
        manualSection: '§6.5',
    },
    16: {
        hintText: 'Casino. Costs 2 Staff. Base Amenity bonus +1 Snohe; +1 '
            + 'more for Vanguards.',
        manualSection: '§6.5',
    },
    17: {
        hintText: 'Upgraded Theater. Costs 1 Staff. Matches the Actors Guild '
            + '— extra +1 Snohe on scored Thespians.',
        manualSection: '§6.5',
    },
    18: {
        hintText: 'Theater. Costs 2 Staff. Base Amenity bonus +1 Snohe; +1 '
            + 'more for Thespians.',
        manualSection: '§6.5',
    },
    19: {
        hintText: 'Upgraded Bar. Costs 1 Staff. Matches the Bar Association '
            + 'Guild — extra +1 Snohe on scored Bar Patrons. A Staffed Bar '
            + 'immunizes the Hotel against unsatisfied Patrons\' Snohe drain.',
        manualSection: '§6.5',
    },
    20: {
        hintText: 'Bar. Costs 2 Staff. Base Amenity bonus +1 Snohe; +1 more '
            + 'for Patrons. Staffed Bar provides Patron-complication '
            + 'immunity.',
        manualSection: '§6.5',
    },
    21: {
        hintText: 'Upgraded Snohe Altar. Costs 1 Staff. Matches The Church '
            + 'Guild — extra +1 Snohe on scored Worshipers.',
        manualSection: '§6.5',
    },
    22: {
        hintText: 'Snohe Altar. Costs 2 Staff. Base Amenity bonus +1 Snohe; '
            + '+1 more for Worshipers.',
        manualSection: '§6.5',
    },
    23: {
        hintText: 'Upgraded Menagerie. Costs 1 Staff. Matches the Seeker '
            + 'Society Guild — extra +1 Snohe on scored Seekers.',
        manualSection: '§6.5',
    },
    24: {
        hintText: 'Menagerie. Costs 2 Staff. Base Amenity bonus +1 Snohe; +1 '
            + 'more for Seekers.',
        manualSection: '§6.5',
    },

    // ===================== Faction Beds (Manual §6.2) ================
    25: {
        hintText: 'Upgraded Deep Bed. Costs 1 Staff. +1 Snohe for any '
            + 'satisfied Guest, +2 if the Guest is Deep.',
        manualSection: '§6.2',
    },
    26: {
        hintText: 'Deep Bed. Costs 2 Staff. Same scoring as Upgraded. '
            + 'Upgrade via Research to reduce staffing cost.',
        manualSection: '§6.2',
    },
    27: {
        hintText: 'Upgraded Stem Bed. Costs 1 Staff. +1 Snohe, +2 for Stem '
            + 'Guests.',
        manualSection: '§6.2',
    },
    28: {
        hintText: 'Stem Bed. Costs 2 Staff. +1 Snohe, +2 for Stem Guests.',
        manualSection: '§6.2',
    },
    29: {
        hintText: 'Upgraded Hex Bed. Costs 1 Staff. +1 Snohe, +2 for Hex '
            + 'Guests.',
        manualSection: '§6.2',
    },
    30: {
        hintText: 'Hex Bed. Costs 2 Staff. +1 Snohe, +2 for Hex Guests.',
        manualSection: '§6.2',
    },
    31: {
        hintText: 'Upgraded Dread Bed. Costs 1 Staff. +1 Snohe, +2 for Dread '
            + 'Guests.',
        manualSection: '§6.2',
    },
    32: {
        hintText: 'Dread Bed. Costs 2 Staff. +1 Snohe, +2 for Dread Guests.',
        manualSection: '§6.2',
    },
    33: {
        hintText: 'Upgraded Honored Bed. Costs 1 Staff. +1 Snohe, +2 for '
            + 'Honored Guests.',
        manualSection: '§6.2',
    },
    34: {
        hintText: 'Honored Bed. Costs 2 Staff. +1 Snohe, +2 for Honored '
            + 'Guests.',
        manualSection: '§6.2',
    },
    35: {
        hintText: 'Upgraded Seed Bed. Costs 1 Staff. +1 Snohe, +2 for Seed '
            + 'Guests.',
        manualSection: '§6.2',
    },
    36: {
        hintText: 'Seed Bed. Costs 2 Staff. +1 Snohe, +2 for Seed Guests.',
        manualSection: '§6.2',
    },

    // =================== Lodging Permits (Manual §7.2.2) =============
    37: {
        hintText: 'Upgraded Deep Lodging Permit. Authorizes Deep Guests. +1 '
            + 'Snohe to every scored Deep Guest.',
        manualSection: '§7.2.2.1',
    },
    38: {
        hintText: 'Deep Lodging Permit. Authorizes Deep Guests. Upgrade via '
            + 'a Lodging action\'s Supply event.',
        manualSection: '§7.2.2',
    },
    39: {
        hintText: 'Upgraded Stem Lodging Permit. Authorizes Stem Guests. +1 '
            + 'Snohe to every scored Stem Guest.',
        manualSection: '§7.2.2.1',
    },
    40: {
        hintText: 'Stem Lodging Permit. Authorizes Stem Guests.',
        manualSection: '§7.2.2',
    },
    41: {
        hintText: 'Upgraded Hex Lodging Permit. Authorizes Hex Guests. +1 '
            + 'Snohe to every scored Hex Guest.',
        manualSection: '§7.2.2.1',
    },
    42: {
        hintText: 'Hex Lodging Permit. Authorizes Hex Guests.',
        manualSection: '§7.2.2',
    },
    43: {
        hintText: 'Upgraded Dread Lodging Permit. Authorizes Dread Guests. '
            + '+1 Snohe to every scored Dread Guest.',
        manualSection: '§7.2.2.1',
    },
    44: {
        hintText: 'Dread Lodging Permit. Authorizes Dread Guests.',
        manualSection: '§7.2.2',
    },
    45: {
        hintText: 'Upgraded Honored Lodging Permit. Authorizes Honored '
            + 'Guests. +1 Snohe to every scored Honored Guest.',
        manualSection: '§7.2.2.1',
    },
    46: {
        hintText: 'Honored Lodging Permit. Authorizes Honored Guests.',
        manualSection: '§7.2.2',
    },
    47: {
        hintText: 'Upgraded Seed Lodging Permit. Authorizes Seed Guests. +1 '
            + 'Snohe to every scored Seed Guest.',
        manualSection: '§7.2.2.1',
    },
    48: {
        hintText: 'Seed Lodging Permit. Authorizes Seed Guests.',
        manualSection: '§7.2.2',
    },

    // ===================== Weakness Rooms (Manual §3.1) ==============
    49: {
        hintText: 'Dread Weakness: Research. Dread\'s Weakness slot. Acting '
            + 'as a Research room when staffed, but at the punitive 3-Staff '
            + 'cost. Cannot be moved or upgraded.',
        manualSection: '§3.1',
    },
    50: {
        hintText: 'Honored Weakness: Lodging. Honored\'s Weakness slot. Acts '
            + 'as a Lodging room when staffed; 3-Staff cost; cannot be moved '
            + 'or upgraded.',
        manualSection: '§3.1',
    },
    51: {
        hintText: 'Seed Weakness: Staff. Seed\'s Weakness slot. Acts as a '
            + 'Staff room when staffed; 3-Staff cost; cannot be moved or '
            + 'upgraded.',
        manualSection: '§3.1',
    },
    52: {
        hintText: 'Deep Weakness: Renovation. Deep\'s Weakness slot. Acts as '
            + 'a Renovation room when staffed; 3-Staff cost; cannot be moved '
            + 'or upgraded.',
        manualSection: '§3.1',
    },
    53: {
        hintText: 'Stem Weakness: Advertisement. Stem\'s Weakness slot. Acts '
            + 'as an Advertisement room when staffed; 3-Staff cost; cannot '
            + 'be moved or upgraded.',
        manualSection: '§3.1',
    },
    54: {
        hintText: 'Hex Weakness: Ghost. Hex\'s Weakness slot. Acts as a '
            + 'Ghost room when staffed; 3-Staff cost; cannot be moved or '
            + 'upgraded.',
        manualSection: '§3.1',
    },

    // ===================== Other Rooms / Tokens ======================
    55: {
        hintText: 'Random Action Room. A baseline printed slot on the '
            + 'console. When Staffed, generates one Random Action per turn — '
            + 'the First Player rolls the Random Action Die to determine the '
            + 'action type. Independent of any Action Set. Revealed if the '
            + 'covering Core Action Room is sold or moved.',
        manualSection: '§7.3',
    },
    56: {
        hintText: 'Snohe Crash. Valuable debris that starts on your console '
            + '(2 per player). Uninstall via Renovate to move it to your '
            + 'Personal Supply; Process via Research for 3 Snohe. Can also '
            + 'be relocated.',
        manualSection: '§7.2.1',
    },
    57: {
        hintText: 'Upgraded Generic Bed. Costs 1 Staff. +1 Snohe per '
            + 'satisfied non-Patron Guest (no Faction bonus — Generic Beds '
            + 'do not match any Faction).',
        manualSection: '§6.2',
    },
    58: {
        hintText: 'Generic Bed. Costs 2 Staff. Each player starts with one '
            + 'of these installed. Same +1 Snohe scoring as Upgraded. If '
            + 'sold, it returns to a shared Game Pool and can be reacquired '
            + 'by any player.',
        manualSection: '§6.2',
    },

    // ================= Action Hex slices (Manual §7.2) ===============
    71: {
        hintText: 'Research action. Acquire / Upgrade / Sell a Room, or '
            + 'Process a Snohe Crash for 3 Snohe. Deep\'s Strength.',
        manualSection: '§7.2.1',
    },
    72: {
        hintText: 'Lodging action. Roll Fate to Supply your Hotel (permits '
            + 'gained/upgraded) or Attack an opponent (permits lost '
            + 'right-to-left). Stem\'s Strength.',
        manualSection: '§7.2.2',
    },
    73: {
        hintText: 'Staff action. Clock In one Chargeable Staff from supply '
            + 'to an available Room slot. 5th and 6th Clock-Ins require an '
            + 'Upgraded Staffed Staff Action Room. Hex\'s Strength.',
        manualSection: '§7.2.3',
    },
    74: {
        hintText: 'Renovation action. Install / Uninstall / Relocate a Room, '
            + 'or Sell an upgraded installed Room for 3 Snohe. '
            + 'Dread\'s Strength.',
        manualSection: '§7.2.4',
    },
    75: {
        hintText: 'Advertisement action. Target the outermost Guest in a '
            + 'column. Dock, Refer to an opponent, Discard, or fill an empty '
            + 'Orbit. Honored\'s Strength.',
        manualSection: '§7.2.5',
    },
    76: {
        hintText: 'Ghost action. Cleanse your Docks (Fate roll) or Haunt an '
            + 'opponent (Fate roll). Seed\'s Strength.',
        manualSection: '§7.2.6',
    },

    // ===================== Faction icons (Manual §12.5) ==============
    77: {
        hintText: 'Deep. Aquatic species, memetic communication. '
            + 'Strength: Research. Weakness: Renovation. '
            + 'Color: purple (#400248).',
        manualSection: '§12.5.1',
    },
    78: {
        hintText: 'Stem. Spore-based sentients, "The Feed" network. '
            + 'Strength: Lodging. Weakness: Advertisement. '
            + 'Color: orange (#E25B02).',
        manualSection: '§12.5.2',
    },
    79: {
        hintText: 'Hex. Insectoid founders of the Great Engram Collective. '
            + 'Strength: Staff. Weakness: Ghost. Color: yellow (#F1BF39).',
        manualSection: '§12.5.3',
    },
    80: {
        hintText: 'Dread. Marsupials, eight thumbs, engineers. '
            + 'Strength: Renovation. Weakness: Research. '
            + 'Color: red (#8C2614).',
        manualSection: '§12.5.4',
    },
    81: {
        hintText: 'Honored. Reptilians, Snohe Altar network. '
            + 'Strength: Advertisement. Weakness: Lodging. '
            + 'Color: blue (#4B4C85).',
        manualSection: '§12.5.5',
    },
    82: {
        hintText: 'Seed. Reptile-Avian pilgrims, ghost-touched homeworld. '
            + 'Strength: Ghost. Weakness: Staff. Color: green (#46601A).',
        manualSection: '§12.5.6',
    },

    // ===================== Guest Cards (Manual §9.2) =================
    84: {
        hintText: 'Deep Scientist. Needs Deep Lodging Permit + adjacent '
            + 'Staffed Bed. On satisfied score: free Observatory Research '
            + 'bounty.',
        manualSection: '§9.2.2',
    },
    85: {
        hintText: 'Deep Vanguard. On Advertise: gain a free Deep Lodging '
            + 'Permit (or upgrade). Unsatisfied: attacks your Lodging (Fate, '
            + 'right-to-left). Needs Deep Permit + Bed when docked.',
        manualSection: '§9.2.1',
    },
    86: {
        hintText: 'Deep Thespian. Needs Deep Lodging Permit + adjacent '
            + 'Staffed Bed. On satisfied score: free Theater Research bounty.',
        manualSection: '§9.2.2',
    },
    87: {
        hintText: 'Deep Patron. Needs only a Deep Lodging Permit — no Bed '
            + 'required. Unsatisfied: Fate roll loses you Snohe. Staffed Bar '
            + 'immunizes against this.',
        manualSection: '§9.2.1',
    },
    88: {
        hintText: 'Deep Worshiper. Needs Deep Lodging Permit + adjacent '
            + 'Staffed Bed. On satisfied score: free Snohe Altar Research '
            + 'bounty.',
        manualSection: '§9.2.2',
    },
    89: {
        hintText: 'Deep Seeker. May dock on a Ghosted dock, displacing the '
            + 'Ghost. If two Ghosts were cleared, +1 Snohe immediately to '
            + 'the Advertising player. Unsatisfied: forces a Ghost roll on '
            + 'the host. Needs Deep Permit + Bed when scoring.',
        manualSection: '§9.2.1',
    },
    90: {
        hintText: 'Stem Scientist. Needs Stem Lodging + Bed. On score: free '
            + 'Observatory Research.',
        manualSection: '§9.2.2',
    },
    91: {
        hintText: 'Stem Vanguard. On Advertise: free Stem Lodging Permit. '
            + 'Unsatisfied: attacks Lodging.',
        manualSection: '§9.2.1',
    },
    92: {
        hintText: 'Stem Thespian. Needs Stem Lodging + Bed. On score: free '
            + 'Theater Research.',
        manualSection: '§9.2.2',
    },
    93: {
        hintText: 'Stem Patron. Needs only Stem Lodging Permit. Unsatisfied: '
            + 'Snohe loss (Fate). Staffed Bar = immunity.',
        manualSection: '§9.2.1',
    },
    94: {
        hintText: 'Stem Worshiper. Needs Stem Lodging + Bed. On score: free '
            + 'Snohe Altar Research.',
        manualSection: '§9.2.2',
    },
    95: {
        hintText: 'Stem Seeker. May dock on Ghosted dock, displacing the '
            + 'Ghost. Unsatisfied: Ghost roll on host.',
        manualSection: '§9.2.1',
    },
    96: {
        hintText: 'Hex Scientist. Needs Hex Lodging + Bed. On score: free '
            + 'Observatory Research.',
        manualSection: '§9.2.2',
    },
    97: {
        hintText: 'Hex Vanguard. On Advertise: free Hex Lodging Permit. '
            + 'Unsatisfied: attacks Lodging.',
        manualSection: '§9.2.1',
    },
    98: {
        hintText: 'Hex Thespian. Needs Hex Lodging + Bed. On score: free '
            + 'Theater Research.',
        manualSection: '§9.2.2',
    },
    99: {
        hintText: 'Hex Patron. Needs only Hex Lodging Permit. Unsatisfied: '
            + 'Snohe loss (Fate). Staffed Bar = immunity.',
        manualSection: '§9.2.1',
    },
    100: {
        hintText: 'Hex Worshiper. Needs Hex Lodging + Bed. On score: free '
            + 'Snohe Altar Research.',
        manualSection: '§9.2.2',
    },
    101: {
        hintText: 'Hex Seeker. May dock on Ghosted dock, displacing the '
            + 'Ghost. Unsatisfied: Ghost roll on host.',
        manualSection: '§9.2.1',
    },
    102: {
        hintText: 'Dread Scientist. Needs Dread Lodging + Bed. On score: '
            + 'free Observatory Research.',
        manualSection: '§9.2.2',
    },
    103: {
        hintText: 'Dread Vanguard. On Advertise: free Dread Lodging Permit. '
            + 'Unsatisfied: attacks Lodging.',
        manualSection: '§9.2.1',
    },
    104: {
        hintText: 'Dread Thespian. Needs Dread Lodging + Bed. On score: free '
            + 'Theater Research.',
        manualSection: '§9.2.2',
    },
    105: {
        hintText: 'Dread Patron. Needs only Dread Lodging Permit. '
            + 'Unsatisfied: Snohe loss (Fate). Staffed Bar = immunity.',
        manualSection: '§9.2.1',
    },
    106: {
        hintText: 'Dread Worshiper. Needs Dread Lodging + Bed. On score: '
            + 'free Snohe Altar Research.',
        manualSection: '§9.2.2',
    },
    107: {
        hintText: 'Dread Seeker. May dock on Ghosted dock, displacing the '
            + 'Ghost. Unsatisfied: Ghost roll on host.',
        manualSection: '§9.2.1',
    },
    108: {
        hintText: 'Honored Scientist. Needs Honored Lodging + Bed. On score: '
            + 'free Observatory Research.',
        manualSection: '§9.2.2',
    },
    109: {
        hintText: 'Honored Vanguard. On Advertise: free Honored Lodging '
            + 'Permit. Unsatisfied: attacks Lodging.',
        manualSection: '§9.2.1',
    },
    110: {
        hintText: 'Honored Thespian. Needs Honored Lodging + Bed. On score: '
            + 'free Theater Research.',
        manualSection: '§9.2.2',
    },
    111: {
        hintText: 'Honored Patron. Needs only Honored Lodging Permit. '
            + 'Unsatisfied: Snohe loss (Fate). Staffed Bar = immunity.',
        manualSection: '§9.2.1',
    },
    112: {
        hintText: 'Honored Worshiper. Needs Honored Lodging + Bed. On score: '
            + 'free Snohe Altar Research.',
        manualSection: '§9.2.2',
    },
    113: {
        hintText: 'Honored Seeker. May dock on Ghosted dock, displacing the '
            + 'Ghost. Unsatisfied: Ghost roll on host.',
        manualSection: '§9.2.1',
    },
    114: {
        hintText: 'Seed Scientist. Needs Seed Lodging + Bed. On score: free '
            + 'Observatory Research.',
        manualSection: '§9.2.2',
    },
    115: {
        hintText: 'Seed Vanguard. On Advertise: free Seed Lodging Permit. '
            + 'Unsatisfied: attacks Lodging.',
        manualSection: '§9.2.1',
    },
    116: {
        hintText: 'Seed Thespian. Needs Seed Lodging + Bed. On score: free '
            + 'Theater Research.',
        manualSection: '§9.2.2',
    },
    117: {
        hintText: 'Seed Patron. Needs only Seed Lodging Permit. Unsatisfied: '
            + 'Snohe loss (Fate). Staffed Bar = immunity.',
        manualSection: '§9.2.1',
    },
    118: {
        hintText: 'Seed Worshiper. Needs Seed Lodging + Bed. On score: free '
            + 'Snohe Altar Research.',
        manualSection: '§9.2.2',
    },
    119: {
        hintText: 'Seed Seeker. May dock on Ghosted dock, displacing the '
            + 'Ghost. Unsatisfied: Ghost roll on host.',
        manualSection: '§9.2.1',
    },

    // ===================== Ghost Cards (Manual §7.2.6) ===============
    120: {
        hintText: 'Two-Ghost (Upgraded) Ghost Card. A Dock with two Ghosts '
            + 'flips to this face. Drains 1 Snohe during the Guest Phase '
            + '(2 Ghosts = -1 Snohe via the Ghost Penalty). A Dock can hold '
            + 'at most one Ghost card.',
        manualSection: '§7.2.6',
    },
    121: {
        hintText: 'One-Ghost Ghost Card. Placed face-up on a Dock when the '
            + 'first Ghost arrives. A second Ghost on the same Dock flips it '
            + 'to the two-Ghost side. Cleansed Ghost cards return to the '
            + 'owner\'s personal supply.',
        manualSection: '§7.2.6',
    },
};

export function getTextureHint(tname) {
    return TEXTURE_HINTS[tname] || null;
}
